/**
 * MODULE: domain/pages/frontend/components/editor/PagesTasksSyncBlockView.tsx
 * PURPOSE: Live CRM tasks list block synced to the current page via taskApi tags
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Label, Spinner, TextInput, ToggleSwitch } from 'flowbite-react';
import { HiOutlineClipboardList, HiOutlinePlus, HiRefresh } from 'react-icons/hi';
import { useIsPagesPublicView } from '../../context/PagesEditorViewModeContext';
import { usePageTasksSync } from '../../hooks/usePageTasksSync';
import {
  formatTaskDueDate,
  isTaskComplete,
  taskPriorityBadgeClass,
  taskPriorityLabel,
} from '../../utils/pageTasksSyncUtils';

export function PagesTasksSyncBlockView({ node, selected, updateAttributes, editor }: NodeViewProps) {
  const navigate = useNavigate();
  const isPublicView = useIsPagesPublicView();
  const editable = editor.isEditable && !isPublicView;
  const pageId = String(node.attrs.pageId ?? '');
  const title = String(node.attrs.title ?? 'Tasks');
  const showCompleted = Boolean(node.attrs.showCompleted);
  const [draftTitle, setDraftTitle] = useState('');

  const { tasks, isLoading, isError, isFetching, refetch, createTask, toggleTaskDone } =
    usePageTasksSync({
      pageId,
      showCompleted,
      enabled: Boolean(pageId) && !isPublicView,
    });

  const handleAddTask = () => {
    const nextTitle = draftTitle.trim();
    if (!nextTitle || createTask.isPending) return;
    createTask.mutate(nextTitle, {
      onSuccess: () => setDraftTitle(''),
    });
  };

  return (
    <NodeViewWrapper
      className={`my-4 ${selected ? 'ring-2 ring-blue-300 ring-offset-2 dark:ring-blue-700 dark:ring-offset-gray-950' : ''}`}
      data-testid="pages-tasks-sync-block"
      data-task-count={tasks.length}
    >
      <section className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
              <HiOutlineClipboardList className="h-4 w-4" aria-hidden />
            </span>
            {editable ? (
              <TextInput
                sizing="sm"
                value={title}
                onChange={(event) => updateAttributes({ title: event.target.value })}
                className="max-w-[220px]"
                data-testid="pages-tasks-sync-title-input"
              />
            ) : (
              <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isPublicView ? (
              <>
                <ToggleSwitch
                  checked={showCompleted}
                  label="Show completed"
                  onChange={(checked) => updateAttributes({ showCompleted: checked })}
                  data-testid="pages-tasks-sync-show-completed"
                />
                <Button
                  size="xs"
                  color="light"
                  onClick={() => void refetch()}
                  disabled={isFetching}
                  data-testid="pages-tasks-sync-refresh"
                >
                  <HiRefresh className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
                </Button>
              </>
            ) : null}
            {!isPublicView ? (
              <Button size="xs" color="light" onClick={() => navigate('/crm')} data-testid="pages-tasks-sync-open-crm">
                Open CRM
              </Button>
            ) : null}
          </div>
        </header>

        <div className="px-4 py-3">
          {isPublicView ? (
            <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="pages-tasks-sync-public-note">
              Linked tasks are available when you open this page in the workspace.
            </p>
          ) : isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner size="md" aria-label="Loading tasks" />
            </div>
          ) : isError ? (
            <p className="text-sm text-red-600 dark:text-red-400" data-testid="pages-tasks-sync-error">
              Could not load tasks. Try refresh.
            </p>
          ) : tasks.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="pages-tasks-sync-empty">
              No linked tasks yet. Add one below — it stays synced with this page in CRM.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="pages-tasks-sync-list">
              {tasks.map((task) => {
                const done = isTaskComplete(task);
                const dueLabel = formatTaskDueDate(task.due_date);
                return (
                  <li
                    key={task.id}
                    className="flex items-start gap-3 rounded-md border border-gray-100 px-2 py-2 dark:border-gray-800"
                    data-testid={`pages-tasks-sync-item-${task.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={done}
                      disabled={!editable || toggleTaskDone.isPending}
                      onChange={() => toggleTaskDone.mutate(task)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                      aria-label={`Mark "${task.title}" complete`}
                      data-testid={`pages-tasks-sync-checkbox-${task.id}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`truncate text-sm font-medium ${
                            done
                              ? 'text-gray-500 line-through dark:text-gray-400'
                              : 'text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          {task.title}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${taskPriorityBadgeClass(task.priority)}`}
                        >
                          {taskPriorityLabel(task.priority)}
                        </span>
                      </div>
                      {dueLabel ? (
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Due {dueLabel}</p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {editable ? (
            <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
              <div className="min-w-[200px] flex-1">
                <Label htmlFor={`pages-tasks-sync-add-${pageId}`} className="sr-only">
                  New task
                </Label>
                <TextInput
                  id={`pages-tasks-sync-add-${pageId}`}
                  sizing="sm"
                  placeholder="Add a task linked to this page…"
                  value={draftTitle}
                  onChange={(event) => setDraftTitle(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddTask();
                    }
                  }}
                  data-testid="pages-tasks-sync-add-input"
                />
              </div>
              <Button
                size="sm"
                color="blue"
                disabled={!draftTitle.trim() || createTask.isPending}
                onClick={handleAddTask}
                data-testid="pages-tasks-sync-add-button"
              >
                <HiOutlinePlus className="mr-1 h-4 w-4" aria-hidden />
                Add task
              </Button>
            </div>
          ) : null}
        </div>
      </section>
    </NodeViewWrapper>
  );
}
